import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Input, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { MdChevronRight, MdExpandMore, MdFolder, MdFolderOpen, MdStar, MdChevronLeft, MdPerson, MdHistory, MdDescription, MdSchedule } from "react-icons/md";
import classNames from "classnames";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  findSectionForFolder,
  findAncestorIds,
} from "../utils/buildReportFoldersTree";
import "./ReportsFolderSidebar.css";

const filterTree = (nodes, query, getLabel) => {
  if (!query) return nodes;
  const q = query.toLowerCase();
  return nodes
    .map((node) => {
      const label = (getLabel(node) || "").toLowerCase();
      if (label.includes(q)) {
        return { ...node, children: node.children || [] };
      }
      const children = filterTree(node.children || [], query, getLabel);
      if (children.length > 0) {
        return { ...node, children };
      }
      return null;
    })
    .filter(Boolean);
};

const TreeRow = ({
  node,
  level,
  expandedIds,
  selectedId,
  ancestorIds,
  onToggle,
  onSelect,
  t,
}) => {
  const isSection = node.type === "section";
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const isActiveParent = !isSelected && ancestorIds?.has(node.id);
  const label = node.nameKey ? t(node.nameKey) : node.name;
  const paddingLeft = 4 + level * 16;
  const rowRef = useRef(null);
  const labelRef = useRef(null);
  const [showRecentTooltip, setShowRecentTooltip] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (node.type !== "recent-report" || !labelRef.current) {
        setShowRecentTooltip(false);
        return;
      }
      setShowRecentTooltip(labelRef.current.scrollWidth > labelRef.current.clientWidth);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [label, node.type]);

  const handleMouseEnter = useCallback(() => {
    if (node.type !== "recent-report" || !showRecentTooltip || !rowRef.current) return;
    const rect = rowRef.current.getBoundingClientRect();
    setTooltipStyle({
      left: rect.right + 8,
      top: rect.top + rect.height / 2,
    });
    setTooltipOpen(true);
  }, [node.type, showRecentTooltip]);

  const handleMouseLeave = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  return (
    <>
      <button
        ref={rowRef}
        type="button"
        className={classNames("reports-folder-sidebar__row", {
          "reports-folder-sidebar__row--section": isSection,
          "reports-folder-sidebar__row--selected": isSelected,
          "reports-folder-sidebar__row--active-parent": isActiveParent,
        })}
        style={{ paddingLeft }}
        onClick={() => onSelect(node)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={label}
      >
        {hasChildren ? (
          <span
            className="reports-folder-sidebar__chevron"
            aria-hidden
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle(node.id);
            }}
            role="presentation"
          >
            {isExpanded ? <MdExpandMore size={17} /> : <MdChevronRight size={17} />}
          </span>
        ) : (
          <span className="reports-folder-sidebar__chevron-spacer" aria-hidden />
        )}
        <span className="reports-folder-sidebar__icon" aria-hidden>
          {isSection && node.tab === "favorites" ? (
            <MdStar size={16} />
          ) : isSection && node.id === "recent-root" ? (
            <MdHistory size={16} />
          ) : isSection && node.tab === "schedule" ? (
            <MdSchedule size={16} />
          ) : node.type === "recent-report" ? (
            <MdDescription size={16} />
          ) : node.type === "owner" ? (
            <MdPerson size={16} />
          ) : isExpanded && hasChildren ? (
            <MdFolderOpen size={16} />
          ) : (
            <MdFolder size={16} />
          )}
        </span>
        <span ref={node.type === "recent-report" ? labelRef : undefined} className="reports-folder-sidebar__label">
          {label}
        </span>
        {typeof node.reportCount === "number" ? (
          <span className="reports-folder-sidebar__count">{node.reportCount}</span>
        ) : null}
      </button>
      {tooltipOpen && tooltipStyle && typeof document !== "undefined"
        ? ReactDOM.createPortal(
            <div
              className="reports-folder-sidebar__floating-tooltip"
              style={tooltipStyle}
            >
              {label}
            </div>,
            document.body
          )
        : null}
      {hasChildren && isExpanded
        ? node.children.map((child) => (
            <TreeRow
              key={child.id}
              node={child}
              level={level + 1}
              expandedIds={expandedIds}
              selectedId={selectedId}
              ancestorIds={ancestorIds}
              onToggle={onToggle}
              onSelect={onSelect}
              t={t}
            />
          ))
        : null}
    </>
  );
};

export const ReportsFolderSummary = ({
  activeColor = "blue",
  sectionLabel,
  folderLabel,
  reportCount,
  onNewFolder,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const accent = activeColorStyle.borderColor || "var(--theme-tertiary-states)";

  return (
    <div
      className="reports-folder-summary"
      style={{ "--reports-folder-accent": accent }}
    >
      <div className="reports-folder-summary__meta">
        {sectionLabel ? (
          <span className="reports-folder-summary__crumb">{sectionLabel}</span>
        ) : null}
        <span className="reports-folder-summary__title">
          {folderLabel || t("reports.folders.allReports")}
          {typeof reportCount === "number"
            ? ` · ${t("reports.folders.reportCount", { count: reportCount })}`
            : ""}
        </span>
      </div>
      <div className="reports-folder-summary__actions">
        <Button
          size="sm"
          color="link"
          className="p-0"
          style={{ color: accent, fontWeight: 600, fontSize: 12 }}
          onClick={onNewFolder}
        >
          + {t("reports.folders.newFolder")}
        </Button>
      </div>
    </div>
  );
};

const ReportsFolderSidebar = ({
  activeColor = "blue",
  collapsed = false,
  onCollapsedChange,
  selectedFolderId,
  onSelectFolder,
  allowedTabs,
  folders: foldersProp,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const accent = activeColorStyle.borderColor || "var(--theme-tertiary-states)";

  const folders = useMemo(() => {
    const tree = Array.isArray(foldersProp) ? foldersProp : [];
    if (!allowedTabs?.length) return tree;
    return tree.filter(
      (section) => section.tab == null || allowedTabs.includes(section.tab)
    );
  }, [foldersProp, allowedTabs]);

  const [folderSearch, setFolderSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => {
    const initial = new Set();
    folders.forEach((s) => initial.add(s.id));
    return initial;
  });

  useEffect(() => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      folders.forEach((s) => {
        if (!next.has(s.id) && s.type === "section") next.add(s.id);
      });
      return next;
    });
  }, [folders]);

  useEffect(() => {
    if (!selectedFolderId) return;
    const ancestors = findAncestorIds(folders, selectedFolderId) || [];
    if (!ancestors.length) return;
    setExpandedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      ancestors.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedFolderId, folders]);

  const getNodeLabel = useCallback(
    (node) => (node.nameKey ? t(node.nameKey) : node.name || ""),
    [t]
  );

  const visibleTree = useMemo(
    () => filterTree(folders, folderSearch.trim(), getNodeLabel),
    [folders, folderSearch, getNodeLabel]
  );

  const ancestorIds = useMemo(() => {
    if (!selectedFolderId) return new Set();
    const ids = findAncestorIds(folders, selectedFolderId) || [];
    return new Set(ids);
  }, [folders, selectedFolderId]);

  useEffect(() => {
    const q = folderSearch.trim();
    if (!q) return;
    const collectExpandable = (nodes, acc = []) => {
      nodes.forEach((n) => {
        if (n.children?.length) {
          acc.push(n.id);
          collectExpandable(n.children, acc);
        }
      });
      return acc;
    };
    const ids = collectExpandable(visibleTree);
    if (!ids.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, [folderSearch, visibleTree]);

  const handleToggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (node) => {
      if (node?.children?.length) {
        setExpandedIds((prev) => {
          if (prev.has(node.id)) return prev;
          const next = new Set(prev);
          next.add(node.id);
          return next;
        });
      }
      if (typeof onSelectFolder === "function") onSelectFolder(node);
    },
    [onSelectFolder]
  );

  const handleCollapsedSectionClick = useCallback(
    (node) => {
      handleSelect(node);
      onCollapsedChange?.(false);
    },
    [handleSelect, onCollapsedChange]
  );

  if (collapsed) {
    return (
      <aside
        className="reports-folder-sidebar reports-folder-sidebar--collapsed"
        style={{ "--reports-folder-accent": accent }}
      >
        <div className="reports-folder-sidebar__collapsed-rail">
          <button
            type="button"
            className="reports-folder-sidebar__collapse-btn"
            onClick={() => onCollapsedChange?.(false)}
            aria-label={t("reports.folders.expandPanel")}
            title={t("reports.folders.expandPanel")}
          >
            <MdChevronRight size={18} />
          </button>
          {folders.map((section) => {
            const label = section.nameKey ? t(section.nameKey) : section.name;
            const isActive =
              selectedFolderId === section.id ||
              (Boolean(selectedFolderId?.startsWith("recent-")) &&
                section.id === "recent-root") ||
              (Boolean(selectedFolderId) &&
                findSectionForFolder(folders, selectedFolderId)?.id === section.id);
            const Icon =
              section.id === "recent-root"
                ? MdHistory
                : section.tab === "favorites"
                  ? MdStar
                  : section.tab === "schedule"
                    ? MdSchedule
                    : section.tab === "custom"
                      ? MdFolderOpen
                      : MdFolder;
            return (
              <button
                key={section.id}
                type="button"
                className={classNames("reports-folder-sidebar__rail-btn", {
                  "reports-folder-sidebar__rail-btn--active": isActive,
                })}
                onClick={() => handleCollapsedSectionClick(section)}
                aria-label={label}
                title={label}
              >
                <Icon size={18} />
              </button>
            );
          })}
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="reports-folder-sidebar"
      style={{ "--reports-folder-accent": accent }}
    >
      <div className="reports-folder-sidebar__header">
        <h3 className="reports-folder-sidebar__title">{t("reports.folders.title")}</h3>
        <button
          type="button"
          className="reports-folder-sidebar__collapse-btn"
          onClick={() => onCollapsedChange?.(true)}
          aria-label={t("reports.folders.collapsePanel")}
          title={t("reports.folders.collapsePanel")}
        >
          <MdChevronLeft size={18} />
        </button>
      </div>
      <div className="reports-folder-sidebar__search">
        <Input
          type="text"
          value={folderSearch}
          onChange={(e) => setFolderSearch(e.target.value)}
          placeholder={t("reports.folders.searchPlaceholder")}
          className="form-control"
        />
      </div>
      <div className="reports-folder-sidebar__tree">
        {visibleTree.length === 0 ? (
          <div className="reports-folder-sidebar__empty">{t("reports.folders.noFolders")}</div>
        ) : (
          visibleTree.map((node) => (
            <React.Fragment key={node.id}>
              <TreeRow
                node={node}
                level={0}
                expandedIds={expandedIds}
                selectedId={selectedFolderId}
                ancestorIds={ancestorIds}
                onToggle={handleToggle}
                onSelect={handleSelect}
                t={t}
              />
              {node.id === "recent-root" &&
              !(node.children && node.children.length) ? (
                <div
                  className="reports-folder-sidebar__empty"
                  style={{ padding: "4px 8px 10px 36px", textAlign: "left" }}
                >
                  {t("reports.folders.noRecent")}
                </div>
              ) : null}
            </React.Fragment>
          ))
        )}
      </div>
    </aside>
  );
};

export default ReportsFolderSidebar;
